import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query'
import { getInboxPage, getLoanThreads } from 'services/messages'
import { LOAN_THREADS_KEY, UNSEEN_COUNT_KEY, inboxKey } from 'modules/inbox'

/**
 * The member's messages, newest first, narrowed by time and by type.
 *
 * Each filter is its own cache entry, so flipping back to one already seen is
 * instant. The first page carries the unseen count, which keeps the footer
 * badge honest without a second request.
 */
export default function useInbox(
    period: TInboxPeriod | undefined,
    category: TInboxCategory | undefined
) {
    const queryClient = useQueryClient()
    return useInfiniteQuery(
        inboxKey(period, category),
        ({ pageParam }) =>
            getInboxPage({ before: pageParam, period, category }),
        {
            getNextPageParam: (last) => last.next ?? undefined,
            // A keyset page is one indexed range scan, so unlike the old
            // day-paged endpoint a refetch on focus is cheap — and it is what
            // turns a message read on the phone green here.
            staleTime: 15_000,
            keepPreviousData: true,
            onSuccess: (data) => {
                const count = data.pages[0]?.unseen_count
                if (typeof count === 'number')
                    queryClient.setQueryData(UNSEEN_COUNT_KEY, count)
            },
        }
    )
}

export function useLoanThreads(enabled = true) {
    return useQuery(LOAN_THREADS_KEY, getLoanThreads, {
        enabled,
        staleTime: 30_000,
    })
}
